import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WrenchIcon, UtensilsIcon, ShoppingBagIcon, BriefcaseIcon, HeartPulseIcon, CarIcon, ArrowRightIcon } from 'lucide-react';
interface CategoryGroup {
  id: string;
  label: string;
  icon: React.ReactNode;
  items: string[];
}
const categoryGroups: CategoryGroup[] = [{
  id: 'home',
  label: 'Home services',
  icon: <WrenchIcon className="w-4 h-4" data-id="element-2596" />,
  items: ['Plumbers', 'Electricians', 'HVAC technicians', 'Roofers', 'Landscapers', 'Pest control', 'Cleaning services', 'Painters']
}, {
  id: 'food',
  label: 'Food & beverage',
  icon: <UtensilsIcon className="w-4 h-4" data-id="element-2597" />,
  items: ['Restaurants', 'Cafés & coffee shops', 'Bars & nightlife', 'Bakeries', 'Catering services', 'Food trucks', 'Juice bars', 'Breweries']
}, {
  id: 'retail',
  label: 'Retail & shops',
  icon: <ShoppingBagIcon className="w-4 h-4" data-id="element-2598" />,
  items: ['Clothing stores', 'Grocery stores', 'Pet shops', 'Florists', 'Hardware stores', 'Gift shops', 'Bookstores', 'Pharmacies']
}, {
  id: 'business',
  label: 'Business services',
  icon: <BriefcaseIcon className="w-4 h-4" data-id="element-2599" />,
  items: ['Lawyers', 'Accountants', 'Real estate brokers', 'Insurance agents', 'Financial advisors', 'Tax consultants', 'Mortgage brokers', 'Notaries']
}, {
  id: 'health',
  label: 'Health & wellness',
  icon: <HeartPulseIcon className="w-4 h-4" data-id="element-2600" />,
  items: ['Dentists', 'Chiropractors', 'Dermatologists', 'Physical therapists', 'Optometrists', 'Veterinarians', 'Mental health counselors', 'Urgent care clinics']
}, {
  id: 'auto',
  label: 'Automotive',
  icon: <CarIcon className="w-4 h-4" data-id="element-2601" />,
  items: ['Auto repair shops', 'Car dealerships', 'Auto body shops', 'Tire shops', 'Car washes', 'Oil change services', 'Towing companies', 'Auto parts stores']
}];
export function CategoryList() {
  const [activeTab, setActiveTab] = useState('home');
  const activeGroup = categoryGroups.find(g => g.id === activeTab)!;
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2602">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-16 relative overflow-hidden" data-id="element-2603">
        {/* Subtle dot pattern */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle, #334155 1px, transparent 1px)',
        backgroundSize: '20px 20px'
      }} data-id="element-2604" />

        <div className="relative z-10" data-id="element-2605">
          {/* Header */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5
        }} className="text-center max-w-3xl mx-auto mb-10" data-id="element-2606">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2607">
              <span className="text-blue-600" data-id="element-2608">Coverage</span>{' '}
              <span className="text-slate-900 font-bold" data-id="element-2609">
                across all local business types
              </span>
            </h2>
            <p className="text-lg text-slate-500" data-id="element-2610">
              Access verified contacts for 500+ business categories worldwide
            </p>
          </motion.div>

          {/* Tab bar */}
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.4,
          delay: 0.15
        }} className="flex justify-center mb-10" data-id="element-2611">
            <div className="inline-flex items-center gap-1 p-1.5 bg-slate-50 border border-slate-200/80 rounded-full" data-id="element-2612">
              {categoryGroups.map(group => <button key={group.id} onClick={() => setActiveTab(group.id)} className={`relative flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap ${activeTab === group.id ? 'text-white' : 'text-blue-600 hover:bg-white hover:shadow-sm'}`} data-id="element-2613">
                  {activeTab === group.id && <motion.div layoutId="activeTabBg" className="absolute inset-0 bg-blue-600 rounded-full" transition={{
                type: 'spring',
                stiffness: 400,
                damping: 30
              }} data-id="element-2614" />}
                  <span className="relative z-10 flex items-center gap-2" data-id="element-2615">
                    {group.icon}
                    <span className="hidden sm:inline" data-id="element-2616">{group.label}</span>
                  </span>
                </button>)}
            </div>
          </motion.div>

          {/* Category grid */}
          <div className="max-w-4xl mx-auto" data-id="element-2617">
            <AnimatePresence mode="wait" data-id="element-2618">
              <motion.div key={activeTab} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.25
            }} className="grid grid-cols-2 md:grid-cols-4" data-id="element-2619">
                {activeGroup.items.map((item, idx) => <motion.a key={idx} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.3,
                delay: idx * 0.05
              }} href="#/data" className="group flex items-center gap-3 px-5 py-4 border-b border-slate-100 md:border-r last:border-r-0 hover:bg-blue-50/40 transition-colors duration-200" style={{
                borderRight: (idx + 1) % 4 === 0 ? 'none' : undefined
              }} data-id="element-2620">
                    <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0 group-hover:scale-125 transition-transform duration-200" data-id="element-2621" />
                    <span className="text-slate-700 font-medium text-[15px] group-hover:text-blue-600 transition-colors duration-200" data-id="element-2622">
                      {item}
                    </span>
                  </motion.a>)}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer */}
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.4,
          delay: 0.3
        }} className="mt-10 text-center flex flex-col items-center gap-4" data-id="element-2623">
            <p className="text-slate-500" data-id="element-2624">
              Covering{' '}
              <span className="text-blue-600 font-bold" data-id="element-2625">
                500+ business categories
              </span>{' '}
              worldwide
            </p>
            <a href="#/data" className="inline-flex items-center gap-2 text-blue-600 font-bold hover:text-blue-700 transition-colors group" data-id="element-2626">
              Explore all categories
              <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-2627" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>;
}